import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SelectItem } from '@mydocma/dynamic-form/lib/fields/filter-select/select-item';
import { MmViewService } from '../../mm-view.service';
import { MmDbService } from '../../mm-db/mm-db-service';
import { MmProjectDbCollections } from '../../mm-db/mm-project-db-schema';
import { Company } from '@docma-mm-core/company/company';
import { Contact } from '@docma-mm-core/contact/contact';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY_FILTER_ID } from './field-mapping.enum';
import { getCompanyInfo } from '@docma-mm-core/company/company';
import { Logger } from '@mydocma/common/src/lib/logger';

@Injectable()
export class FieldsResponsibleFilterService implements OnDestroy {
  
  private currentProjectId: number;
  private companiesSubject = new BehaviorSubject<SelectItem[]>([]);
  private contactsSubject = new BehaviorSubject<SelectItem[]>([]);
  private destroy$ = new Subject<void>();
  
  public companies$ = this.companiesSubject.asObservable();
  public contacts$ = this.contactsSubject.asObservable();
  
  constructor(
    private readonly mmViewService: MmViewService,
    private readonly mmDbService: MmDbService,
    private readonly translateService: TranslateService,
    private readonly logger: Logger
  ) {
    this.initialize();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initialize(): void {
    this.mmViewService.currentProject$
      .pipe(
        filter(project => !!project),
        takeUntil(this.destroy$)
      )
      .subscribe(project => {
        this.currentProjectId = project.Id;
        this.loadProjectData(project.MyDocmaUid);
      });
  }

  private async loadProjectData(projectMyDocmaUid: string): Promise<void> {
    try {
      const db = await this.mmDbService.getProjectDb(projectMyDocmaUid);
      
      await Promise.all([
        this.loadCompanies(db),
        this.loadContacts(db)
      ]);
    } catch (error) {
      this.logger.error(error, '', 'FieldsResponsibleFilterService');
    }
  }

  private async loadCompanies(db: any): Promise<void> {
    try {
      const companies = await db.getAll(MmProjectDbCollections.companies as never) as Company[];
      const selectItems = this.formatCompaniesForDropdown(companies || []);
      this.companiesSubject.next(selectItems);
    } catch (error) {
      this.logger.error(error, '', 'FieldsResponsibleFilterService');
      this.companiesSubject.next([]);
    }
  }

  private async loadContacts(db: any): Promise<void> {
    try {
      const contacts = await db.getAll(MmProjectDbCollections.contacts as never) as Contact[];
      console.log('[FieldsResponsibleFilterService] RESP_FILTER_DEBUG All contacts loaded:', contacts.length, contacts.map(c => ({
        id: c.Id,
        name: `${c.FirstName} ${c.LastName}`,
        IsActive: c.IsActive,
        IsDeleted: c.IsDeleted
      })));
      const activeContacts = contacts.filter(c => c.IsActive !== false && !c.IsDeleted);
      const selectItems = this.formatContactsForDropdown(activeContacts || []);
      this.contactsSubject.next(selectItems);
    } catch (error) {
      this.logger.error(error, '', 'FieldsResponsibleFilterService');
      this.contactsSubject.next([]);
    }
  }

  private formatCompaniesForDropdown(companies: Company[]): SelectItem[] {
    const selectItems: SelectItem[] = [];
    
    selectItems.push({
      id: EMPTY_FILTER_ID,
      text: this.translateService.instant('MM.fields.filter.empty')
    });
    
    const sortedCompanies = companies
      .filter(company => company.Id && company.IsActive !== false)
      .sort((a, b) => (a.Name1 || '').localeCompare(b.Name1 || ''));
    
    sortedCompanies.forEach(company => {
      const shortName = company.ShortName || '';
      const name1 = company.Name1 || '';
      let displayTextBase: string = '';

      if (shortName && name1) {
        displayTextBase = `${shortName} - ${name1}`;
      } else {
        displayTextBase = shortName || name1 || '';
      }

      const displayText = displayTextBase + getCompanyInfo(company, this.translateService);
      
      selectItems.push({
        id: company.Id.toString(),
        text: displayText
      });
    });
    
    return selectItems;
  }

  private formatContactsForDropdown(contacts: Contact[]): SelectItem[] {
    const selectItems: SelectItem[] = [];
    
    selectItems.push({
      id: EMPTY_FILTER_ID,
      text: this.translateService.instant('MM.fields.filter.empty')
    });
    
    const sortedContacts = contacts
      .filter(contact => contact.Id)
      .sort((a, b) => {
        const lastNameCompare = (a.LastName || '').localeCompare(b.LastName || '');
        if (lastNameCompare !== 0) return lastNameCompare;
        return (a.FirstName || '').localeCompare(b.FirstName || '');
      });
    
    sortedContacts.forEach(contact => {
      const firstName = contact.FirstName || '';
      const lastName = contact.LastName || '';
      let displayText = '';
      
      if (firstName && lastName) {
        displayText = `${firstName} ${lastName}`;
      } else if (lastName) {
        displayText = lastName;
      } else if (firstName) {
        displayText = firstName;
      } else {
        console.warn('No display text found for contact:', contact);
        return;
      }
      
      selectItems.push({
        id: contact.Id.toString(),
        text: displayText
      });
    });
    
    return selectItems;
  }

  public getCompanyDisplayNameById(companyId: string): string {
    const companies = this.companiesSubject.value;
    const company = companies.find(c => c.id === companyId);
    return company?.text || companyId;
  }

  public getContactDisplayNameById(contactId: string): string {
    const contacts = this.contactsSubject.value;
    const contact = contacts.find(c => c.id === contactId);
    return contact?.text || contactId;
  }
}